import { Expose, Transform, Type } from 'class-transformer';
import { Feature, Point } from 'geojson';

import { ExposeRawData } from '@lib/common/decorators';
import { SnModel } from '@lib/common/models';

import { SnObjectInfo, SnContentType } from '../base';
import { SnCamera } from '../camera';
import { SnFloor, SnLocation } from '../location';
import { SnUser } from '../user';
import { SnAlarmStatus } from './alarm-status';

export interface SnAlarmMedia {
  id: string;
  url: string;
  createdTimestamp?: string;
  updatedTimestamp?: string;
  file?: any;
  description?: string | null;
  alarm?: string;
}

export class SnAlarm extends SnModel {
  @Expose()
  public id: string;

  @Expose()
  public type: string;

  @Expose()
  public status: SnAlarmStatus;

  @Expose()
  public count: number;

  @Expose()
  public description: string;

  @Expose()
  public message: string;

  @Expose()
  public thumbnailUrl: string;

  @Expose()
  public plateImageUrl?: string;

  @Expose()
  public recordingUrl: string;

  @Expose()
  public recordingUrlType: string;

  @Expose()
  @Type(() => SnLocation)
  public location: SnLocation;

  @Expose()
  @Type(() => SnFloor)
  public floor: SnFloor;

  @Expose()
  @Type(() => SnObjectInfo)
  public sourceInfo: SnObjectInfo;

  @Expose()
  @Transform((value, data) => {
    if (data.sourceInfo && data.sourceInfo.contentType === SnContentType.Camera) {
      return new SnCamera(data.source);
    }

    return data.source;
  })
  public source: SnCamera | Object;

  @Expose()
  @ExposeRawData()
  public details: { [key: string]: any };

  @Expose()
  @ExposeRawData()
  public geometry: Point;

  @Expose()
  public media?: SnAlarmMedia[];

  @Expose()
  public altitude: number;

  @Expose()
  public heading: number;

  @Expose()
  @Type(() => SnUser)
  public ackedBy: SnUser;

  @Expose()
  @Type(() => SnUser)
  public closedBy: SnUser;

  @Expose({ name: 'createdTimestamp' })
  @Type(() => Date)
  public createdAt: Date;

  @Expose({ name: 'updatedTimestamp' })
  @Type(() => Date)
  public updatedAt: Date;

  public autoplay: boolean;

  constructor(data?: any) {
    super();
    this.fromJSON(data);
  }

  get toastDescription(): string {
    let desc = this.type;
    if (this.type === 'Shooter Detection Systems') {
      desc = `Gunshot Detected: ${this.details['Facility']}, Floor: ${this.details['Floor']}`;
    }
    if (this.type.toLowerCase().indexOf('lpr') > -1) {
      desc = `LPR: ${this.details['reason']}`;
    }

    return desc;
  }

  get toastMessage(): string {
    let msg = this.message;
    if (this.type.toLowerCase().indexOf('lpr') > -1) {
      msg = `Plate: ${this.details['plate'] || this.details['Plate']}`;
    }
    return msg;
  }

  get coords(): { lng: number; lat: number } {
    return {
      lng: this.geometry?.coordinates[0],
      lat: this.geometry?.coordinates[1],
    };
  }

  public toGeoJSON(): Feature<Point> {
    return {
      type: 'Feature',
      id: this.id,
      geometry: this.geometry,
      properties: {
        id: this.id,
        type: this.type,
        count: this.count,
        status: this.status,
      },
    };
  }
}

export class SnAlarmHeatmap extends SnModel {
  @Expose() public type: string;

  @Expose() public lon: number;

  @Expose() public lat: number;

  @Expose({ name: 'createdTimestamp' })
  @Type(() => Date)
  public createdAt: Date;

  constructor(data?: any) {
    super();
    this.fromJSON(data);
  }
}
